<template>
	<span>
		<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" max-width="300px" content-class="menu-content">
			<template #activator="{ props }">
				<slot name="button" :props="props"></slot>
			</template>
			<v-card class="menu-card" rounded="lg" elevation="8" @click.stop>
				<v-card-text class="pa-0">
					<v-list>
						<v-list-item
							v-for="option in organizationOptions"
							:key="option.id"
							class="w-100"
							:class="{ 'selected-row': option.id === currentlySelectedItem }"
							@click.stop="toggleSelected(option.id)">
							<template v-slot:prepend>
								<v-list-item-action start>
									<v-checkbox-btn
										:disabled="currentlySelectedItem === option.id"
										:key="option.id"
										v-model="currentlySelectedItem"
										:value="option.id"></v-checkbox-btn>
								</v-list-item-action>
							</template>
							<v-list-item-title>
								{{ option.name }}
							</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-card-text>
				<div class="opacity-50">
					<hr />
				</div>
				<v-card-actions class="d-flex justify-end">
					<PrimaryBtn @click="applyFilter">Apply</PrimaryBtn>
				</v-card-actions>
			</v-card>
		</v-menu>
	</span>
</template>
<script setup lang="ts">
	import { ref, Ref, computed, ComputedRef, watch } from "vue";

	/* Definitions */
	interface OrganizationOption {
		id: OrganizationFilterType;
		name: string;
	}

	export type OrganizationFilterType = "current" | "all";

	/* Vue Props */
	const emit = defineEmits<{
		(e: "filter-organization", organization: OrganizationFilterType): void;
	}>();

	const props = defineProps<{
		preSelectedOrganization: OrganizationFilterType;
		parentType: string;
	}>();

	/* Refs */
	const isFilterOpen: Ref<boolean> = ref(false);
	const selectedOrganization: Ref<OrganizationFilterType> = ref(props.preSelectedOrganization ?? "current");
	const currentlySelectedItem: Ref<OrganizationFilterType> = ref(props.preSelectedOrganization ?? "current");
	const organizationOptions: Ref<Array<OrganizationOption>> = ref([
		{
			id: "current",
			name: "Current Organization" // TODO: Get current organization name
		},
		{
			id: "all",
			name: "All Organizations"
		}
	]);

	/* Watches */
	watch(
		() => props.preSelectedOrganization,
		(value) => {
			currentlySelectedItem.value = value ?? "current";
			selectedOrganization.value = value ?? "current";
		}
	);

	watch(
		() => isFilterOpen.value,
		(value) => {
			if (!value) {
				currentlySelectedItem.value = selectedOrganization.value;
			}
		}
	);
	/* Methods */
	const toggleSelected = (id: OrganizationFilterType) => {
		currentlySelectedItem.value = id;
	};

	function closeMenu() {
		currentlySelectedItem.value = selectedOrganization.value;
		isFilterOpen.value = false;
	}

	const applyFilter = (emitEvent: boolean = true) => {
		selectedOrganization.value = currentlySelectedItem.value;
		closeMenu();

		if (emitEvent) {
			emit("filter-organization", currentlySelectedItem.value);
		}
	};

	// Will reset the filter to the default value.
	function reset() {
		currentlySelectedItem.value = "current";
		selectedOrganization.value = "current";
		applyFilter(false);
	}

	/* Expose */
	defineExpose({
		isFilterOpen: isFilterOpen,
		reset: reset
	});
</script>
